import { c as _c } from "react/compiler-runtime";
import { type UseQueryResult, useQuery, type UseMutationResult, useQueryClient, useMutation, type QueryClient } from '@tanstack/react-query';
import * as standardReports from 'shared/api/standardReports';
import type * as fetch from 'shared/api/fetch';
import * as analytics from 'shared/api/analytics';
export const queryKeys = {
  reportingSolutionsConfigs: 'reportingSolutionsConfigs',
  reportingSolutionsConfigsStatuses: 'reportingSolutionsConfigsStatuses',
  reportingSolutionsDagStatus: 'reportingSolutionsDagStatus'
};
export const useQueryReportingSolutionsConfigs = (clientId, t0) => {
  const $ = _c(7);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId) {
    t1 = [queryKeys.reportingSolutionsConfigs, clientId];
    t2 = async () => await standardReports.getStandardReports(clientId);
    $[0] = clientId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = Boolean(clientId) && enabled;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return useQuery(t4);
};
const getUseCache = (queryClient: QueryClient, clientId: number): boolean => {
  const queriesData = queryClient.getQueriesData({
    queryKey: [queryKeys.reportingSolutionsConfigsStatuses, clientId]
  });
  const useCache = queriesData.every(queryData => queryData?.[1] == null);
  return useCache;
};
export const useQueryReportingSolutionsConfigsStatuses = clientId => {
  const $ = _c(2);
  let t0;
  if ($[0] !== clientId) {
    t0 = {
      queryKey: [queryKeys.reportingSolutionsConfigsStatuses, clientId],
      queryFn: async t1 => {
        const {
          client
        } = t1;
        const useCache = getUseCache(client, clientId);
        return await analytics.postConfigStatuses("transform_client_configs_status_ui", clientId, useCache);
      }
    };
    $[0] = clientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useQuery(t0);
};
export const useQueryReportingSolutionsConfigsStatusById = (clientId, configId, t0) => {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== configId) {
    t1 = [queryKeys.reportingSolutionsConfigsStatuses, clientId, configId];
    t2 = async t3 => {
      const {
        client
      } = t3;
      const useCache = getUseCache(client, clientId);
      return await analytics.postConfigStatusById("transform_config_status_ui", clientId, configId, useCache);
    };
    $[0] = clientId;
    $[1] = configId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== enabled || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      enabled
    };
    $[4] = enabled;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
};
export const useQueryReportingSolutionDagStatusById = (clientId, configId, t0, t1) => {
  const $ = _c(9);
  const enabled = t0 === undefined ? true : t0;
  const useCache = t1 === undefined ? true : t1;
  let t2;
  let t3;
  if ($[0] !== clientId || $[1] !== configId || $[2] !== useCache) {
    t2 = [queryKeys.reportingSolutionsDagStatus, clientId, configId, useCache];
    t3 = async () => await analytics.postConfigStatusById("transform_dag", clientId, configId, useCache);
    $[0] = clientId;
    $[1] = configId;
    $[2] = useCache;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  const t4 = Boolean(clientId) && enabled;
  let t5;
  if ($[5] !== t2 || $[6] !== t3 || $[7] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return useQuery(t5);
};
export const useMutationCreateReportingSolutions = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: standardReports.createStandardReport,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.reportingSolutionsConfigs]
        });
        queryClient.invalidateQueries({
          queryKey: [queryKeys.reportingSolutionsConfigsStatuses]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
interface UpdateRequest {
  data: standardReports.StandardReportReq;
  configId: number;
}
export const useMutationUpdateReportingSolutions = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp,
      onMutate: async updatedConfigData => {
        await queryClient.cancelQueries({
          queryKey: [queryKeys.reportingSolutionsConfigs, updatedConfigData.data.clientId, updatedConfigData.configId]
        });
        const previousConfig = queryClient.getQueryData([queryKeys.reportingSolutionsConfigs, updatedConfigData.data.clientId, updatedConfigData.configId]);
        queryClient.setQueryData([queryKeys.reportingSolutionsConfigs, updatedConfigData.data.clientId, updatedConfigData.configId], {
          ...previousConfig,
          ...updatedConfigData.data
        });
        return {
          previousConfig,
          updatedConfigData
        };
      },
      onError: (_, __, context) => {
        queryClient.setQueryData([queryKeys.reportingSolutionsConfigs, context?.updatedConfigData.data.clientId, context?.updatedConfigData.configId], context?.previousConfig);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.reportingSolutionsConfigs]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
interface BulkUpdateRequest {
  data: standardReports.StandardReportReq;
  configId: number;
}
type BulkUpdateResult = Array<{
  configId: number;
  success: boolean;
  data?: standardReports.StandardReportResp;
  error?: fetch.ClientError;
}>;
export const useMutationBulkUpdateReportingSolutions = t0 => {
  const $ = _c(3);
  const {
    clientId
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== clientId || $[1] !== queryClient) {
    t1 = {
      mutationFn: _temp3,
      onSuccess: async results => {
        results.forEach(result_0 => {
          if (result_0.success && result_0.data != null) {
            queryClient.setQueryData([queryKeys.reportingSolutionsConfigs, clientId, result_0.configId], result_0.data);
          }
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.reportingSolutionsConfigs, clientId]
        });
        await queryClient.invalidateQueries({
          queryKey: [queryKeys.reportingSolutionsConfigsStatuses, clientId]
        });
      }
    };
    $[0] = clientId;
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
export const useQueryReportingSolutionConfigsById = (clientId, configId, t0) => {
  const $ = _c(12);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== clientId || $[1] !== configId) {
    t1 = [queryKeys.reportingSolutionsConfigs, clientId, configId];
    t2 = async () => await standardReports.getStandardReportById(clientId, configId);
    $[0] = clientId;
    $[1] = configId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== clientId || $[5] !== configId || $[6] !== enabled) {
    t3 = enabled && Boolean(clientId) && !Number.isNaN(configId);
    $[4] = clientId;
    $[5] = configId;
    $[6] = enabled;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return useQuery(t4);
};
export const useMutationRunReportConfigNow = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: standardReports.runStandardReportConfig,
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.reportingSolutionsConfigsStatuses]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
type BulkRunResult = Array<{
  configId: number;
  success: boolean;
  error?: fetch.ClientError;
}>;
export const useMutationBulkRunReportConfigNow = t0 => {
  const $ = _c(7);
  const {
    clientId
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== clientId) {
    t1 = async configIds => {
      const runPromises = configIds.map(async configId => {
        ;
        try {
          await standardReports.runStandardReportConfig({
            clientId,
            configId
          });
          return {
            configId,
            success: true
          };
        } catch (t2) {
          const error = t2;
          return {
            configId,
            success: false,
            error: error as fetch.ClientError
          };
        }
      });
      return await Promise.all(runPromises);
    };
    $[0] = clientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== queryClient) {
    t2 = async () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.reportingSolutionsConfigsStatuses]
      });
    };
    $[2] = queryClient;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      mutationFn: t1,
      onSettled: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useMutation(t3);
};
export const useMutationDeleteReportConfig = () => {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: standardReports.deleteStandardReport,
      onMutate: async deletedConfig => {
        await queryClient.cancelQueries({
          queryKey: [queryKeys.reportingSolutionsConfigs]
        });
        const existingConfigs = queryClient.getQueryData([queryKeys.reportingSolutionsConfigs, deletedConfig.clientId]);
        queryClient.setQueryData([queryKeys.reportingSolutionsConfigs, deletedConfig.clientId], oldConfigs => oldConfigs == null ? [] : oldConfigs.filter(config => config.configId !== deletedConfig.configId));
        return {
          existingConfigs
        };
      },
      onError: (_, __, context) => {
        queryClient.setQueryData([queryKeys.reportingSolutionsConfigs], context?.existingConfigs);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [queryKeys.reportingSolutionsConfigs]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
type BulkDeleteResult = Array<{
  configId: number;
  success: boolean;
  error?: fetch.ClientError;
}>;
export const useMutationBulkDeleteReportConfigs = t0 => {
  const $ = _c(8);
  const {
    clientId
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== clientId) {
    t1 = async configIds => {
      const deletePromises = configIds.map(async configId => {
        ;
        try {
          await standardReports.deleteStandardReport({
            clientId,
            configId
          });
          return {
            configId,
            success: true
          };
        } catch (t2) {
          const error = t2;
          return {
            configId,
            success: false,
            error: error as fetch.ClientError
          };
        }
      });
      return await Promise.all(deletePromises);
    };
    $[0] = clientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== clientId || $[3] !== queryClient) {
    t2 = async (results, configIds_0) => {
      results.forEach(result => {
        if (result.success) {
          queryClient.removeQueries({
            queryKey: [queryKeys.reportingSolutionsConfigs, clientId, result.configId]
          });
        }
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.reportingSolutionsConfigs, clientId]
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.reportingSolutionsConfigsStatuses, clientId]
      });
    };
    $[2] = clientId;
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useMutation(t3);
};
async function _temp(params) {
  return await standardReports.updateStandardReport(params.data, params.configId);
}
async function _temp2(updateRequest) {
  ;
  try {
    const result = await standardReports.updateStandardReport(updateRequest.data, updateRequest.configId);
    return {
      configId: updateRequest.configId,
      success: true,
      data: result
    };
  } catch (t0) {
    const error = t0;
    return {
      configId: updateRequest.configId,
      success: false,
      error: error as fetch.ClientError
    };
  }
}
async function _temp3(updates) {
  const updatePromises = updates.map(_temp2);
  return await Promise.all(updatePromises);
}